import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchJsonWithSupabaseAuth, QA_API_DOMAIN } from "@/api/classifier";
import type { FormReadCorrection } from "./corrections";
import { QUERY_KEY } from "./useFormReadCorrections";

export interface ReviewerAnswer {
  items: string[];
  note?: string;
}

type PointerRow = Pick<
  FormReadCorrection,
  "id" | "judge_reading_key" | "reviewer_answer_key" | "reviewed_at" | "updated_at"
>;

const endpoint = (correctionId: string, path: "judge-reading" | "reviewer-answer") =>
  `${QA_API_DOMAIN}/raw-files-viewer/form-read/${correctionId}/${path}`;

export function useJudgeReading(row: PointerRow) {
  return useQuery({
    queryKey: [QUERY_KEY, "judge-reading", row.id, row.updated_at],
    queryFn: async () => {
      const { reading } = await fetchJsonWithSupabaseAuth<{ reading: { handwriting: string } | null }>(
        endpoint(row.id, "judge-reading"),
      );
      return reading;
    },
    enabled: row.judge_reading_key !== null,
    placeholderData: (previous, previousQuery) =>
      row.judge_reading_key !== null && previousQuery?.queryKey[2] === row.id ? previous : undefined,
  });
}

export function useReviewerAnswer(row: PointerRow) {
  return useQuery({
    queryKey: [QUERY_KEY, "reviewer-answer", row.id, row.reviewer_answer_key, row.reviewed_at],
    queryFn: async () => {
      const { answer } = await fetchJsonWithSupabaseAuth<{ answer: ReviewerAnswer | null }>(
        endpoint(row.id, "reviewer-answer"),
      );
      return answer;
    },
    enabled: row.reviewer_answer_key !== null,
  });
}

export function useSaveReviewerAnswer(encounterId: string, correctionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (answer: ReviewerAnswer) =>
      fetchJsonWithSupabaseAuth<{ answer: ReviewerAnswer }>(endpoint(correctionId, "reviewer-answer"), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(answer),
      }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, "encounter", encounterId] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY, "reviewer-answer", correctionId] }),
      ]),
  });
}
